import React, { FC, useMemo, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { ArrowLeft } from 'lucide-react';

import { Guest, GuestStatus } from 'types/guest.types';
import {
  GuestsQueryResponse,
  GuestsQueryVariables,
  INSIGHTS_GUESTS_QUERY
} from 'graphql/queries/dashboard/event_guests';
import { ModalFooter, ModalHeader, ModalTitle } from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { StepBar } from '@epoch-components/step-bar/StepBar';
import ForwardArrowIcon from 'components/common/icons/ForwardArrowIcon';

import { useRSVPControlModalContext } from '../rsvpControlContext';

import { SelectGuestDropdown } from './SelectGuestDropdown';
import { SelectRsvpDropdown } from './SelectRsvpDropdown';

export const SwapGuestModalContent: FC = () => {
  const {
    navigation,
    guest: { firstName },
    eventOrSessionId,
    swapGuest,
    setSwapGuest,
    swapGuestStatus,
    setSwapGuestStatus
  } = useRSVPControlModalContext();
  const baseGuestsQueryVariables: GuestsQueryVariables = {
    eventId: eventOrSessionId,
    statuses: [GuestStatus.CONFIRMED, GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY],
    searchText: '',
    departments: [],
    currentPage: 0,
    attendanceFilters: []
  };
  const [searchText, setSearchText] = useState('');
  const { data, loading, refetch } = useQuery<GuestsQueryResponse, GuestsQueryVariables>(
    INSIGHTS_GUESTS_QUERY,
    { variables: baseGuestsQueryVariables }
  );

  // Debounce search
  // Revisit this, it's janky
  useEffect(() => {
    const timeOutId = setTimeout(() => refetch({ ...baseGuestsQueryVariables, searchText }), 500);
    return (): void => clearTimeout(timeOutId);
  }, [searchText]);

  const fetchedGuests = useMemo((): Guest[] => data?.guests.guests || [], [data]);
  return (
    <>
      <ModalHeader className="tw-m-0 tw-flex tw-flex-col tw-gap-2">
        <button
          className="tw-flex tw-w-fit tw-flex-row tw-items-center tw-gap-1 tw-p-0 tw-text-medium-grey"
          onClick={navigation.onClickBack}
          type="button"
        >
          <ArrowLeft />
          <span>Back</span>
        </button>
        <ModalTitle className="tw-mb-0 tw-text-2xl tw-font-medium">Swap with guest</ModalTitle>
      </ModalHeader>
      <div className="tw-flex tw-flex-col tw-gap-3 ">
        <span className="tw-font-medium">Select a guest to swap spots with {firstName}</span>
        <SelectGuestDropdown
          loading={loading}
          guests={fetchedGuests}
          selectedGuest={swapGuest}
          onSelect={setSwapGuest}
          onSearch={setSearchText}
        />
      </div>
      {swapGuest && (
        <div className="tw-flex tw-flex-col tw-gap-3 ">
          <span className="tw-font-medium">{`Change ${swapGuest.firstName}'s status to:`}</span>
          <SelectRsvpDropdown
            statusOptions={[GuestStatus.INTERESTED, GuestStatus.CANCELLED, GuestStatus.WAITLISTED]}
            selectedStatus={swapGuestStatus}
            onSelect={setSwapGuestStatus}
          />
        </div>
      )}
      <ModalFooter className="tw-mt-0 tw-flex tw-flex-row tw-justify-end">
        <div className="tw-flex tw-flex-1 tw-items-center">
          <StepBar
            totalSteps={navigation.totalStepNumber}
            currentStep={navigation.currentStepNumber}
          />
        </div>
        <div className="tw-flex tw-flex-row tw-justify-center tw-gap-4">
          <Button
            size="sm"
            onClick={navigation.onClickBack}
            className="tw-flex-1 tw-rounded-[8px]"
            variant="secondary"
          >
            Back
          </Button>
          <Button
            size="sm"
            className="tw-flex-1 tw-rounded-[8px]"
            variant="primary"
            onClick={navigation.onClickSubmit}
            disabled={!swapGuest}
          >
            Next
            <div className="tw-ml-2">
              <ForwardArrowIcon color="white" />
            </div>
          </Button>
        </div>
      </ModalFooter>
    </>
  );
};
